/**
 * The record's page — customer, items, actions and the record-lane panels —
 * framed by PageFrame: on the record lane (a quote-class record on a
 * process_engine deployment) the strip (SA-461), the process band (SA-460)
 * and the two columns of sections through RecordColumns (SA-447); every
 * other record one column at the form width, with today's header, Actions
 * card and boxes. Moved out of OrdersPage unchanged by the E4 page split
 * (SA-397, spec §9.3, assessment risk 8).
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { cn } from '@/utils'
import { ArrowLeft, MessageSquare } from 'lucide-react'
import { Button, Input, NativeSelect, Textarea } from '@/components/ui'
import { PageFrame } from '@/components/layout/PageFrame'
import { IntakePanel } from '@/components/orders/IntakePanel'
import { FeasibilityPanel } from '@/components/orders/FeasibilityPanel'
import { QueuePanel } from '@/components/orders/QueuePanel'
import {
  RecordEnquiryCards, RecordProcessSection, RecordWorkCards, RecordWorkModuleCards,
} from '@/components/records/RecordDetailCards'
import { RecordColumns } from '@/components/records/RecordColumns'
import { RecordSection } from '@/components/records/RecordSection'
import { OUTCOME_CHOICES, outcomeLabel } from '@/components/process/stepLiveText'
import { RecordStrip } from '@/components/records/RecordStrip'
import { TasksSection } from '@/components/records/TasksSection'
import { FeasibilitySection } from '@/components/records/FeasibilitySection'
import { ActivitySection } from '@/components/records/ActivitySection'
import { ladderActions } from '@/components/records/ladderActions'
import {
  customerSummary, deliverySummary, detailsSummary, itemsSummary,
  notesSummary, outcomeSummary, paymentsSummary, recordSummary,
} from '@/components/records/sectionSummaries'
import { useFeatureStore } from '@/stores/featureStore'
import { usePermission } from '@/hooks/usePermission'
import { formatCurrency } from '@/utils/currency'
import { LANDING_RING } from '@/components/records/landingRing'
import { StatusBadge } from './StatusBadge'
import type {
  Order, QuoteOutcome, OrderFeasibilityState, FeasibilityPayload, QueueState, WaitingOn,
  OrderEventItem, OrderMeasures, GateState, OrderGatesPayload,
  IntakeRead, IntakeUpdatePayload, IntakeExtraction, CustomerUpdate, CustomerUpdateApplyPayload,
} from '@/types/commerce'

export function OrderDetail({
  order,
  onBack,
  onUpdateStatus,
  onEnterQuote,
  onUpdateNotes,
  onRecordPayment,
  onCloseOutcome,
  onReinstate,
  onEdit,
  getFeasibility,
  recordFeasibility,
  selectScenario,
  onQueueChanged,
  updateQueueState,
  assignOrder,
  getOrderEvents,
  getOrderMeasures,
  getAssignees,
  resolveDuplicate,
  getOrderGates,
  updateGateState,
  canStartProcess,
  showCommerce,
  noun,
  focusPanel,
  onRecordChanged,
  getIntake,
  updateIntake,
  linkIntakeCompany,
  extractIntake,
  onIntakeChanged,
  processRefreshToken,
  assigneeNames,
  getCustomerUpdates,
  extractCustomerUpdate,
  applyCustomerUpdate,
  dismissCustomerUpdate,
}: {
  order: Order
  onBack: () => void
  onUpdateStatus: (status: string) => Promise<void>
  onEnterQuote: (amountCents: number, notes?: string) => Promise<void>
  onUpdateNotes: (notes: string) => Promise<void>
  onRecordPayment: (amountCents: number, method: string) => Promise<void>
  onCloseOutcome: (outcome: QuoteOutcome, note?: string) => Promise<void>
  /** SA-785: undo a Not an enquiry (the record page's Reinstate). */
  onReinstate: () => Promise<void>
  onEdit: () => void
  getFeasibility: (orderId: string) => Promise<OrderFeasibilityState>
  recordFeasibility: (orderId: string, p: FeasibilityPayload) => Promise<OrderFeasibilityState>
  selectScenario: (orderId: string, scenarioId: string) => Promise<OrderFeasibilityState>
  onQueueChanged: () => void
  updateQueueState: (
    orderId: string, p: { queueState: QueueState; waitingOn?: WaitingOn; reason: string },
  ) => Promise<Order>
  assignOrder: (orderId: string, userId: string | null) => Promise<Order>
  getOrderEvents: (orderId: string, limit?: number) => Promise<OrderEventItem[]>
  getOrderMeasures: (orderId: string) => Promise<OrderMeasures>
  getAssignees: () => Promise<Array<{ id: string; name: string }>>
  resolveDuplicate: (
    orderId: string, p: { action: 'proceed_new' | 'reuse_delta'; matchedOrderId?: string; reason: string },
  ) => Promise<Order>
  getOrderGates: (orderId: string) => Promise<OrderGatesPayload>
  updateGateState: (
    orderId: string, department: string, p: { state: GateState; reason?: string },
  ) => Promise<OrderGatesPayload>
  canStartProcess: boolean
  showCommerce: boolean
  /** The deployment's record noun (E4 spec D22): the page's copy wears it. */
  noun: { singular: string; plural: string }
  /** `?panel=<section id>` (spec §7.5): on the record lane any section's id;
   * the form frame knows `feasibility` only. */
  focusPanel?: string | null
  /** An E4 record card wrote something that can move the record or its
   * process (a sent quote, a response, a status override). */
  onRecordChanged: () => void
  getIntake: (orderId: string) => Promise<IntakeRead>
  updateIntake: (orderId: string, payload: IntakeUpdatePayload) => Promise<IntakeRead>
  /** SA-1456 (spec §7.5): link a company slot. Handed to the record lane's Intake only: the picker
   * reads React Query, which the form frame never mounts. */
  linkIntakeCompany?: (orderId: string, slot: string, companyId: string | null) => Promise<IntakeRead>
  extractIntake: (agenda: string, text: string) => Promise<IntakeExtraction>
  onIntakeChanged: () => void
  processRefreshToken: number
  /** SA-460: id → display name, for the drawer's "assigned to <name>". */
  assigneeNames?: Record<string, string>
  /** SA-1029: the customer-update callbacks, handed to both Intake mounts. */
  getCustomerUpdates?: (orderId: string) => Promise<CustomerUpdate[]>
  extractCustomerUpdate?: (orderId: string, updateId: string) => Promise<CustomerUpdate>
  applyCustomerUpdate?: (orderId: string, updateId: string, payload: CustomerUpdateApplyPayload) => Promise<CustomerUpdate>
  dismissCustomerUpdate?: (orderId: string, updateId: string, reason?: string) => Promise<CustomerUpdate>
}) {
  const [quoteAmount, setQuoteAmount] = useState('')
  const [quoteNotes, setQuoteNotes] = useState('')
  const [notesText, setNotesText] = useState(order.notes || '')
  const [actionLoading, setActionLoading] = useState(false)
  const [showPayForm, setShowPayForm] = useState(false)
  const [payAmount, setPayAmount] = useState('')
  const [payMethod, setPayMethod] = useState('')
  const [outcomeValue, setOutcomeValue] = useState<QuoteOutcome | ''>('')
  const [outcomeNote, setOutcomeNote] = useState('')
  // SA-785 (spec D2): Reinstate is offered on the outcome and the permission
  // closing needs; the server refuses everything else with a sentence.
  const canWrite = usePermission('inbox:write')
  // SA-190: the useOrders `error` banner only renders in the list view, so
  // each action surfaces its failure inline, next to the form that failed.
  const [actionErrors, setActionErrors] = useState<{
    status?: string; quote?: string; notes?: string; payment?: string; outcome?: string
  }>({})
  // SA-377: the record process panel needs a react-query provider its own
  // sibling test files don't set up — before-load defaults to hidden
  // (rather than useFeature's normal "assume enabled" flash-avoidance
  // default) so mounting it never races ahead of the feature manifest.
  const processEnabled = useFeatureStore((s) => s.isLoaded && s.features.has('process_engine'))
  // E4 (SA-397, spec §9.3): the record lane — a quote-class record on a
  // process_engine deployment. It gets the process panel and the E4 cards
  // (all React Query); every other record keeps today's page.
  const recordLane = processEnabled && order.orderType === 'quote'
  // SA-460 (spec §6.4, §7.5): `?step=<id>` names the open step drawer, beside
  // `?order=` and `?panel=`. Written with `replace`, never `push` (SA-454's
  // rule); closing removes it; `handleBack`'s `{}` clears it with the rest.
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedStep = searchParams.get('step')
  // SA-1328 (spec §4): the task page's Done sends `?land=process`; read it, drop it from the address.
  // Tied to the record it was written for: if the band never becomes ready, a later record opened in this instance is not handed it.
  // SA-1338: read whenever the address gains it, not only at mount: the Quotes page's arrival
  // replace (`useBackLanding`) can commit after this page has mounted.
  const [landingFor, setLandingFor] = useState<string | null>(() => (searchParams.get('land') === 'process' ? order.id : null))
  const landing = landingFor === order.id
  const landParam = searchParams.get('land')
  useEffect(() => {
    if (landParam === null) return
    if (landParam === 'process') setLandingFor(order.id)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('land')
      return next
    }, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once each time the address gains `land`
  }, [landParam])
  const selectStep = useCallback((id: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (id) next.set('step', id)
      else next.delete('step')
      return next
    }, { replace: true })
  }, [setSearchParams])
  const feasibilityRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (focusPanel === 'feasibility' && feasibilityRef.current) {
      feasibilityRef.current.scrollIntoView?.({ block: 'start', behavior: 'smooth' })
    }
  }, [focusPanel])

  const setActionError = (key: 'status' | 'quote' | 'notes' | 'payment' | 'outcome', message?: string) =>
    setActionErrors(prev => ({ ...prev, [key]: message }))

  const handleStatusAction = async (status: string) => {
    setActionLoading(true)
    setActionError('status')
    try { await onUpdateStatus(status) }
    catch (e) { setActionError('status', (e as Error).message) }
    finally { setActionLoading(false) }
  }

  const handleQuote = async () => {
    const cents = Math.round(parseFloat(quoteAmount) * 100)
    if (isNaN(cents) || cents <= 0) return
    setActionLoading(true)
    setActionError('quote')
    try { await onEnterQuote(cents, quoteNotes || undefined) }
    catch (e) { setActionError('quote', (e as Error).message) }
    finally { setActionLoading(false) }
  }

  const handleNotes = async () => {
    setActionLoading(true)
    setActionError('notes')
    try { await onUpdateNotes(notesText) }
    catch (e) { setActionError('notes', (e as Error).message) }
    finally { setActionLoading(false) }
  }

  const openPayForm = () => {
    const completed = (order.payments ?? [])
      .filter(p => p.status === 'completed')
      .reduce((sum, p) => sum + p.amountCents, 0)
    const outstanding = Math.max(order.totalCents - completed, 0)
    setPayAmount((outstanding / 100).toFixed(2))
    setPayMethod('')
    setShowPayForm(true)
  }

  const handleRecordPayment = async () => {
    setActionLoading(true)
    setActionError('payment')
    try {
      await onRecordPayment(Math.round(parseFloat(payAmount) * 100), payMethod)
      setShowPayForm(false)
    } catch (e) {
      setActionError('payment', (e as Error).message)
    } finally {
      setActionLoading(false)
    }
  }

  const handleCloseOutcome = async () => {
    if (!outcomeValue) return
    setActionLoading(true)
    setActionError('outcome')
    try { await onCloseOutcome(outcomeValue, outcomeNote || undefined) }
    catch (e) { setActionError('outcome', (e as Error).message) }
    finally { setActionLoading(false) }
  }

  const handleReinstate = async () => {
    if (!window.confirm(`Reinstate ${noun.singular.toLowerCase()} ${order.orderNumber} and return it to the register?`)) return
    setActionLoading(true)
    setActionError('outcome')
    try { await onReinstate() }
    catch (e) { setActionError('outcome', (e as Error).message) }
    finally { setActionLoading(false) }
  }

  // Header
  const header = (
    <div className="flex items-center gap-3">
      <Button variant="ghost" size="sm" aria-label={`Back to ${noun.plural.toLowerCase()}`} onClick={onBack} className="px-2">
        <ArrowLeft className="w-5 h-5" />
      </Button>
      <div className="flex-1">
        <h2 className="text-xl font-bold text-foreground">{order.orderNumber}</h2>
        <p className="text-sm text-muted-foreground">{order.orderType} via {order.channel}</p>
      </div>
      <StatusBadge status={order.status} />
    </div>
  )

  // Customer
  const customerBody = (
    <>
      <p className="text-foreground font-medium">{order.customerName || 'Unknown'}</p>
      {order.customerPhone && <p className="text-sm text-muted-foreground">{order.customerPhone}</p>}
      {order.customerEmail && <p className="text-sm text-muted-foreground">{order.customerEmail}</p>}
      {order.conversationId && (
        <a href={`/inbox/${order.conversationId}`} className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 mt-1">
          <MessageSquare className="w-3.5 h-3.5" /> View conversation
        </a>
      )}
    </>
  )
  const customer = (
    <div className="bg-card rounded-lg p-4 border border-border">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Customer</h3>
      {customerBody}
    </div>
  )

  // Items
  const hasItems = Boolean(order.items && order.items.length > 0)
  const itemsBody = (
    <>
      <ul className="space-y-1">
        {(order.items ?? []).map((item, i) => {
          const qty = Number(item.quantity || 1)
          // unit_price (cents) is what the backend actually writes; price
          // (pesos) is a legacy field with no live writer, kept for old rows.
          const unitCents = item.unit_price != null ? Number(item.unit_price)
            : item.price != null ? Number(item.price) * 100 : null
          return (
            <li key={i} className="text-sm">
              <div className="flex justify-between">
                <span className="text-foreground">{String(item.title || item.name || 'Item')} x{qty}</span>
                {unitCents != null && <span className="text-muted-foreground">{formatCurrency(unitCents * qty, order.currency)}</span>}
              </div>
              {item.note ? (
                <span className="block pl-3 text-[11px] italic text-muted-foreground">
                  {String(item.note)}
                </span>
              ) : null}
            </li>
          )
        })}
      </ul>
      {order.deliveryFeeCents > 0 && (
        <div className="flex justify-between text-sm mt-2 pt-2 border-t border-border">
          <span className="text-muted-foreground">Delivery fee</span>
          <span className="text-muted-foreground">{formatCurrency(order.deliveryFeeCents, order.currency)}</span>
        </div>
      )}
      <div className="flex justify-between font-semibold mt-2 pt-2 border-t border-border">
        <span>Total</span>
        <span>{formatCurrency(order.totalCents, order.currency)}</span>
      </div>
    </>
  )
  const items = hasItems && (
    <div className="bg-card rounded-lg p-4 border border-border">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Items</h3>
      {itemsBody}
    </div>
  )

  // Delivery — commerce only (E4, spec D26)
  const hasDelivery = Boolean(showCommerce && order.deliveryMethod)
  const deliveryBody = (
    <>
      <p className="text-sm text-foreground capitalize">{order.deliveryMethod}</p>
      {order.deliveryAddress && <p className="text-sm text-muted-foreground">{order.deliveryAddress}</p>}
      {order.pickupLocation && <p className="text-sm text-muted-foreground">{order.pickupLocation}</p>}
      {order.deliveryTimeWindow && <p className="text-sm text-muted-foreground">Window: {order.deliveryTimeWindow}</p>}
    </>
  )
  const delivery = hasDelivery && (
    <div className="bg-card rounded-lg p-4 border border-border">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Delivery</h3>
      {deliveryBody}
    </div>
  )

  // Custom details
  const hasDetails = Boolean(order.occasion || order.guestCount || order.specialInstructions)
  const detailsBody = (
    <>
      {order.occasion && <p className="text-sm"><span className="text-muted-foreground">Occasion:</span> {order.occasion}</p>}
      {order.guestCount && <p className="text-sm"><span className="text-muted-foreground">Guests:</span> {order.guestCount}</p>}
      {order.specialInstructions && <p className="text-sm mt-1 text-muted-foreground italic">{order.specialInstructions}</p>}
    </>
  )
  const details = hasDetails && (
    <div className="bg-card rounded-lg p-4 border border-border">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Details</h3>
      {detailsBody}
    </div>
  )

  // Quote entry (for pending_quote orders). On the record lane the quote
  // versions card owns quoting: this one-shot box would not move the
  // process, and the legacy endpoint refuses once a version exists
  // (409 quote_versions_in_use).
  const enterQuote = order.status === 'pending_quote' && !recordLane && (
    <div className="bg-warning/10 rounded-lg p-4 border border-warning/20">
      <h3 className="text-sm font-semibold text-warning-strong mb-2">Enter Quote</h3>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Input
            type="number" placeholder="Amount (e.g. 2750)" value={quoteAmount}
            onChange={e => setQuoteAmount(e.target.value)}
          />
          <span className="text-sm text-muted-foreground">{order.currency}</span>
        </div>
        <Textarea
          placeholder="Quote notes (optional)" value={quoteNotes}
          onChange={e => setQuoteNotes(e.target.value)}
          rows={2}
        />
        <Button size="sm" onClick={handleQuote} disabled={actionLoading || !quoteAmount}>
          {actionLoading ? 'Saving...' : 'Submit Quote'}
        </Button>
      </div>
      {actionErrors.quote && <p className="text-sm text-destructive mt-2">{actionErrors.quote}</p>}
    </div>
  )

  // Status actions
  const actions = (
    <div className="bg-card rounded-lg p-4 border border-border">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Actions</h3>
      <div className="flex flex-wrap gap-2">
        {!['delivered', 'cancelled'].includes(order.status) && (
          <Button variant="secondary" size="sm" onClick={onEdit} disabled={actionLoading}>
            Edit {noun.singular.toLowerCase()}
          </Button>
        )}
        {/* E4 (spec D26): every move INTO the commerce ladder is commerce — `ladderActions` is the one rule. */}
        {ladderActions(order, showCommerce).map((a) => (
          <Button key={a.status} size="sm" onClick={() => handleStatusAction(a.status)} disabled={actionLoading}>
            {a.label}
          </Button>
        ))}
        {!['delivered', 'cancelled'].includes(order.status) && (
          <Button variant="ghost" size="sm" onClick={() => handleStatusAction('cancelled')} disabled={actionLoading}
            className="text-destructive hover:bg-destructive/10 hover:text-destructive">
            Cancel {noun.singular}
          </Button>
        )}
      </div>
      {actionErrors.status && <p className="text-sm text-destructive mt-2">{actionErrors.status}</p>}
    </div>
  )

  // SA-401: intake fields — quote-class records only (self-gates).
  // Keyed on order.id (finding 3) so switching records remounts the
  // panel fresh rather than carrying a mid-edit draft across records —
  // belt and braces alongside the panel's own orderId-reset effect.
  // The lane renders its own copy of this panel with `chrome="section"`
  // below; a new prop on the panel reaches both frames only if it is
  // added at both sites.
  const intake = (
    <IntakePanel
      key={order.id}
      orderId={order.id}
      orderType={order.orderType}
      outcome={order.outcome}
      personaId={order.personaId}
      getIntake={getIntake}
      updateIntake={updateIntake}
      extractIntake={extractIntake}
      onIntakeChanged={onIntakeChanged}
      getCustomerUpdates={getCustomerUpdates}
      extractCustomerUpdate={extractCustomerUpdate}
      applyCustomerUpdate={applyCustomerUpdate}
      dismissCustomerUpdate={dismissCustomerUpdate}
      refreshToken={processRefreshToken}
    />
  )

  // SA-268: feasibility panel — quote-class orders only
  const feasibility = (
    <div
      ref={feasibilityRef}
      data-panel="feasibility"
      data-panel-focus={focusPanel === 'feasibility' ? 'true' : undefined}
      className={focusPanel === 'feasibility' ? 'rounded-lg ' + LANDING_RING : undefined}
    >
      <FeasibilityPanel
        orderId={order.id}
        orderType={order.orderType}
        outcome={order.outcome}
        getFeasibility={getFeasibility}
        recordFeasibility={recordFeasibility}
        selectScenario={selectScenario}
        onChanged={onRecordChanged}
      />
    </div>
  )

  // SA-270: mission queue panel — quote-class orders only
  const queue = (
    <QueuePanel
      order={order}
      onChanged={onQueueChanged}
      updateQueueState={updateQueueState}
      assignOrder={assignOrder}
      getOrderEvents={getOrderEvents}
      getOrderMeasures={getOrderMeasures}
      getAssignees={getAssignees}
      resolveDuplicate={resolveDuplicate}
      getOrderGates={getOrderGates}
      updateGateState={updateGateState}
    />
  )

  // SA-266: outcome closure — quote-class orders only
  const outcomeBody = (
    <>
      {order.closedAt ? (
        <div className="text-sm text-muted-foreground">
          <p>{outcomeLabel(order.outcome)}</p>
          {order.outcomeNote && <p className="mt-1">{order.outcomeNote}</p>}
          <p className="mt-1 text-xs">Closed {new Date(order.closedAt).toLocaleString()}</p>
          {order.outcome === 'not_an_enquiry' && canWrite && (
            <Button size="sm" variant="secondary" className="mt-2" onClick={handleReinstate} disabled={actionLoading}>
              Reinstate {noun.singular}
            </Button>
          )}
          {actionErrors.outcome && <p className="text-sm text-destructive mt-2">{actionErrors.outcome}</p>}
        </div>
      ) : (
        <div className="space-y-2">
          <NativeSelect
            aria-label="Outcome"
            value={outcomeValue}
            onChange={e => setOutcomeValue(e.target.value as QuoteOutcome | '')}
          >
            <option value="">Select outcome…</option>
            {OUTCOME_CHOICES.map(o => (
              <option key={o} value={o}>{outcomeLabel(o)}</option>
            ))}
          </NativeSelect>
          <Textarea
            value={outcomeNote}
            onChange={e => setOutcomeNote(e.target.value)}
            placeholder="Outcome note (optional)"
            rows={2}
          />
          <Button size="sm" onClick={handleCloseOutcome} disabled={actionLoading || !outcomeValue}>
            Close {noun.singular}
          </Button>
          {actionErrors.outcome && <p className="text-sm text-destructive mt-2">{actionErrors.outcome}</p>}
        </div>
      )}
    </>
  )
  const outcome = order.orderType === 'quote' && (
    <div className="bg-card rounded-lg p-4 border border-border">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Outcome</h3>
      {outcomeBody}
    </div>
  )

  // Internal notes
  const notesBody = (
    <>
      <div className="space-y-2">
        <Textarea
          value={notesText} onChange={e => setNotesText(e.target.value)}
          rows={3} placeholder={`Add notes about this ${noun.singular.toLowerCase()}...`}
        />
        <Button variant="secondary" size="sm" onClick={handleNotes} disabled={actionLoading}>
          Save Notes
        </Button>
      </div>
      {actionErrors.notes && <p className="text-sm text-destructive mt-2">{actionErrors.notes}</p>}
    </>
  )
  const notes = (
    <div className="bg-card rounded-lg p-4 border border-border">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Internal Notes</h3>
      {notesBody}
    </div>
  )

  // Payments (SA-185: always rendered with commerce — staff record
  // payments here; E4, spec D26: POST /payments 404s without it)
  const paymentsBody = (
    <>
      {order.depositCents > 0 && (
        <p className="text-sm text-muted-foreground mb-2">
          Deposit: {formatCurrency(order.depositCents, order.currency)} — {order.depositPaid ? 'paid' : 'unpaid'}
        </p>
      )}
      {order.payments && order.payments.length > 0 && (
        <ul className="space-y-2 mb-3">
          {order.payments.map(p => (
            <li key={p.id} className="flex justify-between items-center text-sm">
              <div>
                <span className="capitalize">{p.method}</span>
                <span className="text-muted-foreground ml-2">{p.paymentType}</span>
              </div>
              <div className="text-right">
                <span className="font-medium">{formatCurrency(p.amountCents, p.currency)}</span>
                <span className={cn('ml-2 text-xs', p.status === 'completed' ? 'text-success' : 'text-warning-strong')}>{p.status}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
      {showPayForm ? (
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-28">
            <Input aria-label="Payment amount" type="number" min={0} step="0.01"
              value={payAmount} onChange={e => setPayAmount(e.target.value)} />
          </div>
          <div className="w-40">
            <NativeSelect aria-label="Payment method" value={payMethod}
              onChange={e => setPayMethod(e.target.value)}>
              <option value="">Method…</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="bank_transfer">Bank transfer</option>
              <option value="other">Other</option>
            </NativeSelect>
          </div>
          <Button size="sm" onClick={handleRecordPayment}
            disabled={actionLoading || !payMethod || !(parseFloat(payAmount) > 0)}>
            Record
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowPayForm(false)}
            className="text-muted-foreground">
            Cancel
          </Button>
        </div>
      ) : (
        <Button variant="secondary" size="sm" aria-label="Record payment" onClick={openPayForm}>
          Record payment
        </Button>
      )}
      {actionErrors.payment && <p className="text-sm text-destructive mt-2">{actionErrors.payment}</p>}
    </>
  )
  const payments = showCommerce && (
    <div className="bg-card rounded-lg p-4 border border-border">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Payments</h3>
      {paymentsBody}
    </div>
  )

  // Timestamps — a bare div on the form frame; the Record section on the lane.
  const timestampsBody = (
    <>
      {order.createdAt && <p>Created: {new Date(order.createdAt).toLocaleString()}</p>}
      {order.quotedAt && <p>Quoted: {new Date(order.quotedAt).toLocaleString()}</p>}
      {order.paidAt && <p>Paid: {new Date(order.paidAt).toLocaleString()}</p>}
      {order.deliveredAt && <p>Delivered: {new Date(order.deliveredAt).toLocaleString()}</p>}
    </>
  )
  const timestamps = (
    <div className="text-xs text-muted-foreground space-y-0.5">
      {timestampsBody}
    </div>
  )

  // SA-447: the record lane lays the page out as the work beside the enquiry
  // (spec §3); each column keeps today's relative order. Every other record
  // keeps its single column at the form width, cards exactly as before.
  if (recordLane) {
    return (
      <PageFrame width="detail" className="space-y-6 py-6">
        {/* SA-461 (spec §7.1, D11): the strip carries the identity, the facts, the actions and the now line. */}
        <RecordStrip
          order={order} noun={noun} showCommerce={showCommerce} busy={actionLoading} statusError={actionErrors.status}
          assigneeNames={assigneeNames} onBack={onBack} onStatusAction={(s) => void handleStatusAction(s)}
        />
        {/* SA-460 (spec D5): the record's process is a full-width band above the two columns. */}
        <RecordProcessSection
          order={order}
          canStart={canStartProcess}
          refreshToken={processRefreshToken}
          assigneeNames={assigneeNames}
          selectedStep={selectedStep}
          onSelectStep={selectStep}
          onChanged={onRecordChanged}
          landing={landing}
          onLanded={() => setLandingFor(null)}
        />
        {/* SA-461 (spec §7.4): every card a section, in the table's order (ruling R10). */}
        <RecordColumns
          work={<>
            <TasksSection orderId={order.id} noun={noun.singular} focused={focusPanel === 'tasks'} onSelectStep={selectStep} />
            <FeasibilitySection
              focused={focusPanel === 'feasibility'}
              orderId={order.id} orderType={order.orderType} outcome={order.outcome}
              getFeasibility={getFeasibility} recordFeasibility={recordFeasibility} selectScenario={selectScenario}
              onChanged={onRecordChanged}
            />
            <RecordWorkCards order={order} focusPanel={focusPanel} onChanged={onRecordChanged} />
            <QueuePanel
              chrome="section" focused={focusPanel === 'queue'} assigneeNames={assigneeNames ?? {}}
              order={order} onChanged={onQueueChanged} updateQueueState={updateQueueState} assignOrder={assignOrder}
              getOrderEvents={getOrderEvents} getOrderMeasures={getOrderMeasures} getAssignees={getAssignees}
              resolveDuplicate={resolveDuplicate} getOrderGates={getOrderGates} updateGateState={updateGateState}
            />
            <ActivitySection orderId={order.id} focused={focusPanel === 'activity'} />
            <RecordSection id="outcome" title="Outcome" summary={outcomeSummary(order)} defaultOpen={false} focused={focusPanel === 'outcome'}>
              {outcomeBody}
            </RecordSection>
            {showCommerce && (
              <RecordSection id="payments" title="Payments" summary={paymentsSummary(order)} defaultOpen={false} focused={focusPanel === 'payments'}>
                {paymentsBody}
              </RecordSection>
            )}
            <RecordWorkModuleCards order={order} onChanged={onRecordChanged} />
          </>}
          enquiry={<>
            <RecordSection id="customer" title="Customer" summary={customerSummary(order)} defaultOpen={false} focused={focusPanel === 'customer'}>
              {customerBody}
            </RecordSection>
            {hasItems && (
              <RecordSection id="items" title="Items" summary={itemsSummary(order)} defaultOpen={false} focused={focusPanel === 'items'}>{itemsBody}</RecordSection>
            )}
            {hasDelivery && (
              <RecordSection id="delivery" title="Delivery" summary={deliverySummary(order)} defaultOpen={false} focused={focusPanel === 'delivery'}>{deliveryBody}</RecordSection>
            )}
            {hasDetails && (
              <RecordSection id="details" title="Details" summary={detailsSummary(order)} defaultOpen={false} focused={focusPanel === 'details'}>{detailsBody}</RecordSection>
            )}
            <IntakePanel
              key={order.id} chrome="section" defaultOpen={false} focused={focusPanel === 'intake'}
              orderId={order.id} orderType={order.orderType} outcome={order.outcome} personaId={order.personaId}
              getIntake={getIntake} updateIntake={updateIntake} extractIntake={extractIntake} onIntakeChanged={onIntakeChanged}
              getCustomerUpdates={getCustomerUpdates} extractCustomerUpdate={extractCustomerUpdate}
              applyCustomerUpdate={applyCustomerUpdate} dismissCustomerUpdate={dismissCustomerUpdate}
              refreshToken={processRefreshToken} linkIntakeCompany={linkIntakeCompany}
            />
            {/* E4 (SA-397) + SA-451: the module's enquiry cards, the references, the original enquiry. */}
            <RecordEnquiryCards order={order} focusPanel={focusPanel} onChanged={onRecordChanged} />
            {/* SA-1059 (UX pass spec move 1.4): Notes and Record are quiet blocks — no box. */}
            <RecordSection id="notes" title="Notes" chrome="quiet" summary={notesSummary(order.notes)} defaultOpen={false} focused={focusPanel === 'notes'}>
              {notesBody}
            </RecordSection>
            <RecordSection id="record" title="Record" chrome="quiet" summary={recordSummary(order)} defaultOpen={false} focused={focusPanel === 'record'}>
              {timestampsBody}
            </RecordSection>
          </>}
        />
      </PageFrame>
    )
  }

  return (
    <PageFrame width="form" className="space-y-6 py-6">
      {header}
      {customer}
      {items}
      {delivery}
      {details}
      {enterQuote}
      {actions}
      {intake}
      {feasibility}
      {queue}
      {outcome}
      {notes}
      {payments}
      {timestamps}
    </PageFrame>
  )
}
